import {
  LiveReload,
  Outlet,
  LinksFunction,
  Links,
  Scripts,
  Meta,
  ScrollRestoration,
  useLoaderData,
  ErrorBoundaryComponent,
  useCatch,
  useMatches,
  ThrownResponse,
} from "remix";
import tailwindStyles from "./tailwind-gen.css";
import interRomanStyles from "../public/inter.css";
import { LoaderFunction } from "remix";
import { IsDevBuild, TrySearchParam } from "./lib/remixUtil.server";
import { getPool } from "./new_shared/db/db.server";
import { tokenParamName, TntAllUrls, teamEmailAddress } from "./new_shared/urls";
import { CatchBoundaryComponent } from "@remix-run/react/routeModules";
import { getEmailServer } from "./new_shared/email/sendEmail.server";
import { withEffectsSessions } from "./new_shared/effects.server";
import { loginAndRedirect, loginSuccessText } from "./lib/auth.server";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

export const links: LinksFunction = () => {
  return [
    { rel: "stylesheet", href: tailwindStyles },
    { rel: "stylesheet", href: interRomanStyles },
  ];
};

interface LoaderData {
  devBuild: boolean;
}

export const loader: LoaderFunction = async ({ params, request }) => {
  const pool = await getPool();
  const emailServer = await getEmailServer();
  const token = TrySearchParam(request, tokenParamName);
  if (token) {
    /*
      The way this currently works is that it rewrites the URL
      to not include the token search param. 
      
      If the original URL contained any hash params, those seem to be 
      dropped when the search param is changed. So this breaks if the linked URL
      included a hash param

      one solution idea is that we could have a URL that is a "link clicked in email"
      and that would have a param that tells us where to redirect - that redirected URL could
      include a hash param.
    */
    console.log("root loader: found token");
    const originalUrl = new URL(request.url);
    originalUrl.searchParams.delete(tokenParamName);
    originalUrl.protocol = IsDevBuild() ? "http" : "https";
    const ret = await withEffectsSessions(
      pool,
      emailServer,
      async (doEffect) => await loginAndRedirect(doEffect, token, originalUrl.toString(), request),
    );
    // we throw here since we don't want the normal loaders/routes to run at all,
    // but still need to return the response headers so the auth cookie is set
    throw ret;
  }
  return { devBuild: IsDevBuild() };
};

export default function App() {
  const { devBuild } = useLoaderData<LoaderData>();
  const [lastLocation, setLastLocation] = useState("");
  let location = useLocation();
  const matches = useMatches();

  useEffect(() => {
    if (lastLocation == location.pathname) {
      return;
    }
    const routeMatch = matches.find((m) => m.pathname == location.pathname);
    setLastLocation(location.pathname);
    if (devBuild) {
      console.info("not sending pageview in dev");
    } else {
      fetch("/api/pageview", {
        body: JSON.stringify({
          url: location.pathname,
          route: routeMatch?.id,
        }),
        method: "POST",
      }).then((res) => {
        if (res.status != 200) {
          console.error("could not report pageview:", res);
        }
      });
    }
  }, [location]);
  return (
    <html lang="en" className="h-full">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>ThxNoThx: Email Masking &amp; Management</title>
        <Links />
        <Meta />

        {devBuild ? null : (
          <>
            <script async src="https://www.googletagmanager.com/gtag/js?id=UA-212297795-1"></script>
            <script
              dangerouslySetInnerHTML={{
                __html: `
              window.dataLayer = window.dataLayer || []; 
              function gtag() {
                window.dataLayer.push(arguments);
              }
              gtag("js", new Date()); gtag("config",
              "UA-212297795-1");
              `,
              }}
            ></script>
          </>
        )}
      </head>
      <body className="h-full">
        <Scripts />
        <ScrollRestoration />
        <Outlet />
        {devBuild ? <LiveReload /> : null}
        {/* <!-- Start emojicom.io widget --> */}
        <script
          dangerouslySetInnerHTML={{
            __html: `window.emojicom_widget = { campaign: "RcrIeoDWksW48JrRJEhL" };`,
          }}
        ></script>
        <script src="https://cdn.emojicom.io/embed/widget.js" async></script>
        {/* <!-- End emojicom.io widget --> */}
      </body>
    </html>
  );
}

// invoked when there's an exception
export const ErrorBoundary: ErrorBoundaryComponent = ({ error }) => {
  console.error(error);
  return (
    <html>
      <head>
        <title>There was a server error</title>
        <Meta />
        <Links />
      </head>
      <body className="m-8">
        <Scripts />
        <h1>There was a server error</h1>
        <p>
          Please retry, and if the error persists, contact {teamEmailAddress} for help fixing the
          issue.
        </p>
      </body>
    </html>
  );
};

// invoked when a response is thrown
export const CatchBoundary: CatchBoundaryComponent = () => {
  const caught: ThrownResponse = useCatch();
  console.error("CatchBoundary invoked with:", {
    status: caught.status,
    statusText: caught.statusText,
  });

  let content;
  let title;
  if (caught.status == 404) {
    title = "Not found";
    content = <h1>Not found</h1>;
  } else if (caught.status == 200 && caught.data.includes(loginSuccessText)) {
    // in this very specific case, we really just wanted to return the response that was thrown
    // perhaps in the future we should just throw an exception since in the
    //   ErrorBoundary we get the full data of the original
    // I also explored having the Root loader return
    //   data to pass to the root route render method, but
    //   that didn't work (the root route render didn't actually,
    //   which presumably means that we )
    //
    // NB: if you mess with this, you must test on Safari!
    return <html lang="en" dangerouslySetInnerHTML={{ __html: caught.data }}></html>;
  } else {
    title = "A server error has occurred";
    content = (
      <>
        <h1>A server error has occurred</h1>
        <p>
          Please retry, and if the error persists, contact {teamEmailAddress} for help fixing the
          issue.
        </p>
      </>
    );
  }

  return (
    <html>
      <head>
        <title>{title}</title>
        <Meta />
        <Links />
      </head>
      <body className="m-8">
        <Scripts />
        {content}
      </body>
    </html>
  );
};
